import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Button, Space, message, Checkbox, Divider, Typography, Switch, Row, Col, Tag, Collapse } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { dashboardApi } from "../../services/dashboardApi";
import { useTheme } from "../../context/ThemeContext";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const SORT_OPTIONS = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'alphabetical_asc', label: 'Alphabetical (asc)' },
  { value: 'alphabetical_desc', label: 'Alphabetical (desc)' },
  { value: 'numerical_asc', label: 'Numerical (asc)' },
  { value: 'numerical_desc', label: 'Numerical (desc)' },
  { value: 'alphabetical_case_insensitive_asc', label: 'Alphabetical (case-insensitive, asc)' },
  { value: 'alphabetical_case_insensitive_desc', label: 'Alphabetical (case-insensitive, desc)' },
];

const REFRESH_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: 'on_dashboard_load', label: 'On Dashboard Load' },
  { value: 'on_time_range_change', label: 'On Time Range Change' },
];

const VARIABLE_TYPES = [
  { value: 'query', label: 'Query' },
  { value: 'custom', label: 'Custom' },
  { value: 'constant', label: 'Constant' },
  { value: 'textbox', label: 'Text box' },
];

const QUERY_TYPES = [
  { value: 'label_values', label: 'Label values' },
  { value: 'label_names', label: 'Label names' },
  { value: 'metrics', label: 'Metrics' },
  { value: 'query_result', label: 'Query result' },
];

const VariableSettingsModal = ({ visible, variable, onSave, onClose }) => {
  const { isDark } = useTheme();
  const [form] = Form.useForm();
  const [variableType, setVariableType] = useState("query");
  const [queryType, setQueryType] = useState("label_values");
  const [previewValues, setPreviewValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const [metricsList, setMetricsList] = useState([]);
  const [labelsList, setLabelsList] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingLabels, setLoadingLabels] = useState(false);

  // Load metrics when modal opens (for Prometheus)
  useEffect(() => {
    if (!visible || variableType !== 'query') return;
    const loadMetrics = async () => {
      setLoadingMetrics(true);
      try {
        const res = await dashboardApi.searchMetrics('');
        const list = res?.data?.metrics || [];
        setMetricsList(Array.isArray(list) ? list : []);
      } catch {
        setMetricsList([]);
      } finally {
        setLoadingMetrics(false);
      }
    };
    loadMetrics();
  }, [visible, variableType]);

  // Load labels when metric changes (for label_values)
  const selectedMetric = Form.useWatch('metric', form);
  useEffect(() => {
    if (!visible || variableType !== 'query' || queryType !== 'label_values') return;
    if (selectedMetric) {
      setLoadingLabels(true);
      dashboardApi.getMetricLabels(selectedMetric)
        .then((res) => {
          const list = res?.data?.labels || [];
          setLabelsList(Array.isArray(list) ? list.sort((a, b) => String(a).localeCompare(String(b))) : []);
        })
        .catch(() => setLabelsList([]))
        .finally(() => setLoadingLabels(false));
    } else {
      // No metric selected - load global label names
      setLoadingLabels(true);
      dashboardApi.getLabelNames()
        .then((list) => setLabelsList(Array.isArray(list) ? list.sort((a, b) => String(a).localeCompare(String(b))) : []))
        .catch(() => setLabelsList([]))
        .finally(() => setLoadingLabels(false));
    }
  }, [visible, variableType, queryType, selectedMetric, form]);

  useEffect(() => {
    if (variable) {
      form.setFieldsValue({
        name: variable.name,
        label: variable.label,
        description: variable.description || '',
        hide: variable.hide || 0,
        datasource: variable.datasource || 'prometheus',
        labelName: variable.labelName || '',
        metric: variable.metric || '',
        regex: variable.regex || '',
        sort: variable.sort || 'disabled',
        refresh: variable.refresh || 'on_dashboard_load',
        multi: variable.multi || false,
        includeAll: variable.includeAll || false,
        allValue: variable.allValue || '',
      });
      setVariableType(variable.type || 'query');
      setQueryType(variable.queryType || 'label_values');
      if (variable.options) {
        setPreviewValues(variable.options);
      }

      if (variable.selectedDefaults) {
        setSelectedValues(variable.selectedDefaults);
      } else if (variable.current?.value) {
        setSelectedValues(Array.isArray(variable.current.value) ? variable.current.value : [variable.current.value]);
      }
    } else {
      form.resetFields();
      form.setFieldsValue({
        sort: 'disabled',
        refresh: 'on_dashboard_load',
        hide: 0,
        datasource: 'prometheus',
      });
      setPreviewValues([]);
      setSelectedValues([]);
      setVariableType('query');
      setQueryType('label_values');
    }
  }, [variable, form, visible]);

  const applyRegex = (values, regex) => {
    if (!regex) return values;
    try {
      const re = new RegExp(regex);
      return values.map(val => {
        const match = val.match(re);
        if (match) {
          // Support named capture groups
          if (match.groups?.text && match.groups?.value) {
            return { text: match.groups.text, value: match.groups.value };
          }
          return match[1] || val;
        }
        return val;
      }).filter(Boolean);
    } catch (e) {
      return values;
    }
  };

  const sortValues = (values, sortType) => {
    if (sortType === 'disabled') return values;
    
    const sorted = [...values];
    switch (sortType) {
      case 'alphabetical_asc':
        return sorted.sort((a, b) => String(a).localeCompare(String(b)));
      case 'alphabetical_desc':
        return sorted.sort((a, b) => String(b).localeCompare(String(a)));
      case 'numerical_asc':
        return sorted.sort((a, b) => parseFloat(a) - parseFloat(b));
      case 'numerical_desc':
        return sorted.sort((a, b) => parseFloat(b) - parseFloat(a));
      case 'alphabetical_case_insensitive_asc':
        return sorted.sort((a, b) => String(a).toLowerCase().localeCompare(String(b).toLowerCase()));
      case 'alphabetical_case_insensitive_desc':
        return sorted.sort((a, b) => String(b).toLowerCase().localeCompare(String(a).toLowerCase()));
      default:
        return sorted;
    }
  };

  const handlePreview = async () => {
    const labelName = form.getFieldValue("labelName");
    const regex = form.getFieldValue("regex");
    const sort = form.getFieldValue("sort");
    
    if (!labelName && queryType === 'label_values') {
      message.warning("Please enter a label name");
      return;
    }

    setLoading(true);
    try {
      let values = [];
      
      if (queryType === 'label_values') {
        const response = await dashboardApi.getLabelValues(labelName);
        if (response.success && response.data?.values) {
          values = response.data.values;
        }
      } else if (queryType === 'label_names') {
        const response = await dashboardApi.getLabelNames();
        if (response.success && response.data) {
          values = response.data;
        }
      } else if (queryType === 'metrics') {
        const response = await dashboardApi.searchMetrics('');
        if (response.success && response.data?.metrics) {
          values = response.data.metrics;
        }
      }

      // Apply regex if provided
      if (regex) {
        values = applyRegex(values, regex);
      }

      // Apply sorting
      values = sortValues(values, sort);

      setPreviewValues(values);
      message.success(`Found ${values.length} values`);
    } catch (error) {
      console.error("Error loading values:", error);
      message.error("Failed to preview values: " + error.message);
      setPreviewValues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Get default value
      const defaultValue = selectedValues.length > 0 
        ? (values.multi ? selectedValues : selectedValues[0])
        : (previewValues.length > 0 ? previewValues[0] : '');

      // Build query string for backward compatibility
      const metric = values.metric?.trim();
      const labelName = values.labelName?.trim();
      const queryString = queryType === 'label_values' 
        ? (metric ? `label_values(${metric}, ${labelName})` : `label_values(${labelName})`)
        : (labelName || '');

      const variableToSave = {
        ...variable,
        name: values.name,
        label: values.label,
        description: values.description,
        type: variableType,
        queryType,
        datasource: values.datasource,
        labelName: values.labelName,
        metric: values.metric,
        query: queryString,
        regex: values.regex,
        sort: values.sort,
        refresh: values.refresh,
        multi: values.multi,
        includeAll: values.includeAll,
        allValue: values.allValue,
        hide: values.hide,
        options: previewValues,
        current: {
          value: defaultValue,
          text: Array.isArray(defaultValue) ? defaultValue.join(' + ') : defaultValue,
        },
        selectedDefaults: selectedValues,
      };

      onSave(variableToSave);
    } catch (error) {
      console.error("Validation error:", error);
    }
  };

  const sectionStyle = {
    background: isDark ? '#0d1117' : '#fafafa',
    padding: '16px',
    borderRadius: '4px',
    marginBottom: '16px',
    border: isDark ? '1px solid #30363d' : '1px solid #f0f0f0',
  };

  const labelStyle = {
    color: isDark ? '#8b949e' : '#666',
    fontSize: '12px',
    marginBottom: '4px',
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{variable?.name || 'New Variable'}</span>
          {variable?.name && (
            <Tag color="blue" style={{ fontWeight: 'normal' }}>
              {variableType}
            </Tag>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save
        </Button>,
      ]}
      bodyStyle={{
        background: isDark ? '#161b22' : '#fff',
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      <Form form={form} layout="vertical" size="middle">
        {/* Variable Type Selection */}
        <div style={{ marginBottom: 16 }}>
          <Text style={labelStyle}>Select variable type</Text>
          <Select 
            value={variableType} 
            onChange={setVariableType}
            style={{ width: '100%', marginTop: 4 }}
            options={VARIABLE_TYPES}
          />
        </div>

        {/* General Section */}
        <div style={sectionStyle}>
          <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: isDark ? '#e6edf3' : '#262626' }}>
            General
          </Title>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                label={<span style={labelStyle}>Name <Text type="secondary">(Max. 50 characters)</Text></span>}
                name="name" 
                rules={[
                  { required: true, message: 'Name is required' },
                  { max: 50, message: 'Maximum 50 characters' },
                  { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: 'Invalid variable name' }
                ]}
              >
                <Input placeholder="databricks_cluster_id" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label={<span style={labelStyle}>Label <Text type="secondary">(Optional display name)</Text></span>}
                name="label"
              >
                <Input placeholder="Databricks Cluster ID" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            label={<span style={labelStyle}>Description <Text type="secondary">(Descriptive text)</Text></span>}
            name="description"
          >
            <Input.TextArea rows={2} placeholder="Description of this variable..." />
          </Form.Item>

          <Form.Item 
            label={<span style={labelStyle}>Show on dashboard</span>}
            name="hide"
          >
            <Select>
              <Select.Option value={0}>Label and value</Select.Option>
              <Select.Option value={1}>Value only</Select.Option>
              <Select.Option value={2}>Nothing (hidden)</Select.Option>
            </Select>
          </Form.Item>
        </div>

        {/* Query Options Section */}
        {variableType === 'query' && (
          <div style={sectionStyle}>
            <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: isDark ? '#e6edf3' : '#262626' }}>
              Query options
            </Title>

            <Form.Item 
              label={<span style={labelStyle}>Data source</span>}
              name="datasource"
            >
              <Select>
                <Select.Option value="prometheus">
                  <Space>
                    <img src="/prometheus-logo.svg" alt="Prometheus" style={{ width: 16, height: 16 }} onError={(e) => e.target.style.display='none'} />
                    Prometheus
                  </Space>
                </Select.Option>
                <Select.Option value="loki">Loki</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item 
              label={<span style={labelStyle}>Query type</span>}
            >
              <Select value={queryType} onChange={setQueryType} options={QUERY_TYPES} />
            </Form.Item>

            {queryType === 'label_values' && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    label={<span style={labelStyle}>Metric <Text type="secondary">(Optional)</Text></span>}
                    name="metric"
                  >
                    <Select
                      placeholder="Select metric"
                      allowClear
                      showSearch
                      loading={loadingMetrics}
                      filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      options={metricsList.map((m) => ({ label: m, value: m }))}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label={<span style={labelStyle}>Label *</span>}
                    name="labelName"
                    rules={[{ required: true, message: 'Label is required' }]}
                  >
                    <Select
                      placeholder="Select label"
                      showSearch
                      loading={loadingLabels}
                      filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      options={labelsList.map((l) => ({ label: l, value: l }))}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item 
              label={
                <span style={labelStyle}>
                  Regex <Text type="secondary">(Optional - extract part of value)</Text>
                </span>
              }
              name="regex"
              extra={
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Named capture groups can be used: /.*-(?&lt;text&gt;.*)-(?&lt;value&gt;.*)-.*/
                </Text>
              }
            >
              <Input placeholder="/.*-(?<text>.*)-(?<value>.*)-.*/	" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label={<span style={labelStyle}>Sort</span>}
                  name="sort"
                >
                  <Select options={SORT_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  label={<span style={labelStyle}>Refresh</span>}
                  name="refresh"
                >
                  <Select options={REFRESH_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        {/* Selection Options Section */}
        <div style={sectionStyle}>
          <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: isDark ? '#e6edf3' : '#262626' }}>
            Selection options
          </Title>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="multi" 
                valuePropName="checked"
                style={{ marginBottom: 8 }}
              >
                <Checkbox>
                  <div>
                    <div style={{ fontWeight: 500 }}>Multi-value</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Enables multiple values to be selected at the same time
                    </Text>
                  </div>
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="includeAll" 
                valuePropName="checked"
                style={{ marginBottom: 8 }}
              >
                <Checkbox>
                  <div>
                    <div style={{ fontWeight: 500 }}>Include All option</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Enables an option to include all variables
                    </Text>
                  </div>
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            label={<span style={labelStyle}>Custom all value <Text type="secondary">(Optional)</Text></span>}
            name="allValue"
            style={{ marginTop: 8 }}
          >
            <Input placeholder=".*" />
          </Form.Item>
        </div>

        {/* Preview Section */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Title level={5} style={{ margin: 0, color: isDark ? '#e6edf3' : '#262626' }}>
              Preview of values
            </Title>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handlePreview} 
              loading={loading}
              type="primary"
              ghost
            >
              Run Query
            </Button>
          </div>

          {previewValues.length > 0 ? (
            <>
              <div style={{ 
                maxHeight: 180, 
                overflow: 'auto', 
                padding: '12px', 
                background: isDark ? '#0d1117' : '#fff',
                border: isDark ? '1px solid #30363d' : '1px solid #d9d9d9',
                borderRadius: '4px'
              }}>
                <Checkbox.Group
                  style={{ width: '100%' }}
                  value={selectedValues}
                  onChange={setSelectedValues}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {previewValues.map((val, idx) => {
                      const displayValue = typeof val === 'object' ? val.text : val;
                      const actualValue = typeof val === 'object' ? val.value : val;
                      return (
                        <Checkbox key={idx} value={actualValue}>
                          <code style={{ 
                            background: isDark ? '#21262d' : '#f5f5f5',
                            padding: '2px 6px',
                            borderRadius: 3,
                            fontSize: 12
                          }}>
                            {displayValue}
                          </code>
                        </Checkbox>
                      );
                    })}
                  </Space>
                </Checkbox.Group>
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: isDark ? '#8b949e' : '#8c8c8c' }}>
                Found {previewValues.length} value(s) • Selected: {selectedValues.length} as default(s)
              </div>
            </>
          ) : (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center',
              background: isDark ? '#0d1117' : '#fff',
              border: isDark ? '1px solid #30363d' : '1px solid #d9d9d9',
              borderRadius: '4px',
              color: isDark ? '#8b949e' : '#8c8c8c'
            }}>
              Click "Run Query" to preview values
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default VariableSettingsModal;
